import Button from 'components/ui-kits/Button';
import { IButton } from 'components/ui-kits/Button/Button';
import { ArrowLeftIcon, Cross } from 'components/ui-kits/Icon';
import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from 'styles/variables';

type CustomStepperButton = {
  label: string;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    activeStep?: number,
    setActiveStep?: React.Dispatch<React.SetStateAction<number>>
  ) => void;
} & IButton &
  React.ComponentPropsWithoutRef<'button'>;

type StepperProps = {
  steps: {
    id: string;
    content: ReactNode;
    header?: string;
    headerButtonConfig?: {
      closeButton?: Omit<CustomStepperButton, 'label'> & {
        hideCloseButton?: boolean;
      };
    };
    footerConfig?: {
      previousButton?: CustomStepperButton;
      nextButton?: CustomStepperButton;
    };
  }[];
};

const StepperWrapper = styled.div`
  .stepper-content {
    padding: 2rem;
  }

  .divider {
    height: 1px;
    background-color: ${COLORS.GREY_2};
  }

  .accept-terms {
    margin-top: 2rem;
  }
`;
const StepHeader = styled.h2`
  display: flex;
  flex-direction: row;
  align-items: center;

  .back-arrow {
    min-width: 1.5rem;
    padding: 1rem;
    cursor: pointer;
    margin-left: 2rem;
    border-radius: 0.5rem;

    &:hover {
      background-color: ${COLORS.GREY_2};
    }
  }

  .close-button {
    min-width: 1.5rem;
    padding: 1rem;
    cursor: pointer;
    margin-right: 2rem;
    border-radius: 0.5rem;

    &:hover {
      background-color: ${COLORS.GREY_2};
    }
  }

  .step-header {
    font-weight: bold;
    padding: 2rem 2rem 0 2rem;
    margin-bottom: 2rem;
    font-size: 2rem;
    flex: 1;
  }
`;
const FooterButtonWrapper = styled.div`
  padding: 0 2rem;
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Stepper = ({ steps }: StepperProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const currentStepObj = steps[activeStep];
  const closeButton = currentStepObj.headerButtonConfig?.closeButton;
  const hideCloseButton = closeButton?.hideCloseButton;
  const shouldShowCloseButton = !hideCloseButton && activeStep === 0;

  const goToNextStep = () => {
    setActiveStep((prev) => (prev === steps.length - 1 ? prev : prev + 1));
  };

  const goToPreviousStep = () => {
    setActiveStep((prev) => (prev < 1 ? prev : prev - 1));
  };

  return (
    <StepperWrapper key={currentStepObj.id}>
      <StepHeader>
        {activeStep > 0 && (
          <button className="back-arrow" onClick={goToPreviousStep}>
            <ArrowLeftIcon width="1.5rem" />
          </button>
        )}
        {currentStepObj.header ? (
          <h2 className="step-header">{currentStepObj.header}</h2>
        ) : null}
        {shouldShowCloseButton && (
          <button
            {...currentStepObj.headerButtonConfig?.closeButton}
            className="close-button"
            onClick={(event) => {
              typeof currentStepObj.headerButtonConfig?.closeButton?.onClick ===
              'function'
                ? currentStepObj.headerButtonConfig.closeButton.onClick(
                    event,
                    activeStep,
                    setActiveStep
                  )
                : goToPreviousStep();
            }}>
            <Cross width="1.5rem" />
          </button>
        )}
      </StepHeader>
      <div className="divider" />
      <div className="stepper-content">{currentStepObj.content}</div>
      <FooterButtonWrapper>
        <Button
          variant="linkPrimary"
          border={`1px solid ${COLORS.RED}`}
          fontWeight="BOLD"
          {...currentStepObj.footerConfig?.previousButton}
          onClick={(event) => {
            typeof currentStepObj.footerConfig?.previousButton?.onClick ===
            'function'
              ? currentStepObj.footerConfig.previousButton.onClick(
                  event,
                  activeStep,
                  setActiveStep
                )
              : goToPreviousStep();
          }}>
          {!currentStepObj.footerConfig?.previousButton?.label && 'Previous'}
        </Button>
        <Button
          variant="primary"
          fontWeight="BOLD"
          {...currentStepObj.footerConfig?.nextButton}
          onClick={(event) => {
            typeof currentStepObj.footerConfig?.nextButton?.onClick ===
            'function'
              ? currentStepObj.footerConfig.nextButton.onClick(
                  event,
                  activeStep,
                  setActiveStep
                )
              : goToNextStep();
          }}>
          {!currentStepObj.footerConfig?.nextButton?.label && 'Next'}
        </Button>
      </FooterButtonWrapper>
    </StepperWrapper>
  );
};

export default Stepper;
